import { useEffect } from 'react';

// Per-page <head> metadata for a client-rendered app. index.html carries the
// site-wide defaults; a page calls useSeo() to override them while mounted and
// the cleanup restores whatever was there before, so a page that doesn't call
// it never inherits the previous page's title or description.

const SITE_NAME = 'SALT';

export interface SeoOptions {
  /** Page title. The site name is appended unless `bareTitle` is set. */
  title?: string;
  bareTitle?: boolean;
  description?: string;
  /** Absolute URL of the share image. */
  image?: string;
  /** 'article' for news posts, 'website' otherwise. */
  type?: 'website' | 'article';
  /** Emit noindex,nofollow (auth, account, admin, not-found pages). */
  noindex?: boolean;
  /** schema.org JSON-LD object(s) for this page. */
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
}

// www is not a separate site; hi./h3. are (they serve different content).
function canonicalUrl(): string {
  const host = window.location.host.replace(/^www\./i, '');
  return `${window.location.protocol}//${host}${window.location.pathname}`;
}

type Restore = () => void;

function setMeta(attr: 'name' | 'property', key: string, content: string): Restore {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  const created = !el;
  const previous = el?.getAttribute('content') ?? null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return () => {
    if (created) el?.remove();
    else if (previous !== null) el?.setAttribute('content', previous);
  };
}

function setCanonical(href: string): Restore {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  const created = !el;
  const previous = el?.getAttribute('href') ?? null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return () => {
    if (created) el?.remove();
    else if (previous !== null) el?.setAttribute('href', previous);
  };
}

function setJsonLd(data: object): Restore {
  const el = document.createElement('script');
  el.type = 'application/ld+json';
  el.text = JSON.stringify(data);
  document.head.appendChild(el);
  return () => el.remove();
}

export function useSeo(options: SeoOptions): void {
  const { title, bareTitle, description, image, type, noindex, jsonLd } = options;
  // Stable dependency for the object-valued option
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    const restores: Restore[] = [];
    const previousTitle = document.title;

    const fullTitle = title ? (bareTitle ? title : `${title} | ${SITE_NAME}`) : null;
    if (fullTitle) {
      document.title = fullTitle;
      restores.push(setMeta('property', 'og:title', fullTitle));
      restores.push(setMeta('name', 'twitter:title', fullTitle));
    }
    if (description) {
      restores.push(setMeta('name', 'description', description));
      restores.push(setMeta('property', 'og:description', description));
      restores.push(setMeta('name', 'twitter:description', description));
    }
    if (image) {
      restores.push(setMeta('property', 'og:image', image));
      restores.push(setMeta('name', 'twitter:image', image));
    }

    const url = canonicalUrl();
    restores.push(setCanonical(url));
    restores.push(setMeta('property', 'og:url', url));
    restores.push(setMeta('name', 'twitter:url', url));
    if (type) restores.push(setMeta('property', 'og:type', type));
    if (noindex) restores.push(setMeta('name', 'robots', 'noindex,nofollow'));
    if (jsonLdKey) restores.push(setJsonLd(JSON.parse(jsonLdKey)));

    return () => {
      document.title = previousTitle;
      restores.forEach((restore) => restore());
    };
  }, [title, bareTitle, description, image, type, noindex, jsonLdKey]);
}
